import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";

/**
 * Hook to get the current system timezone from GlobalSettings
 * Falls back to browser's timezone if no setting is configured
 */
export function useTimezone() {
  const { data: settings } = useQuery({
    queryKey: ['global-settings'],
    queryFn: () => api.entities.GlobalSettings.list(),
    staleTime: 300000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Browser timezone as fallback (e.g. 'America/New_York')
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const generalSettings = Array.isArray(settings) 
    ? settings.find(s => s.setting_key === 'general_settings')
    : null;

  // Handle setting_value being either a parsed object or a JSON string
  let settingValue = generalSettings?.setting_value;
  if (typeof settingValue === 'string') {
    try {
      settingValue = JSON.parse(settingValue);
    } catch {
      settingValue = null;
    }
  }

  return settingValue?.time_zone || browserTimezone || 'America/Denver';
}

/**
 * Get the short timezone abbreviation (e.g. "MST", "EST", "PST") from an IANA timezone
 */
export function getTimezoneAbbr(timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || timezone;
  } catch {
    return timezone;
  }
}

/**
 * Convert a UTC time string (HH:mm) to local timezone time string (HH:mm)
 */
export function utcTimeToLocal(utcTimeStr, timezone) {
  if (!utcTimeStr) return utcTimeStr;
  const [h, m] = utcTimeStr.split(':').map(Number);
  const now = new Date();
  const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0));
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(utcDate);
  let localHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
  const localMin = parts.find(p => p.type === 'minute')?.value || '00';
  if (localHour === 24) localHour = 0;
  return `${String(localHour).padStart(2, '0')}:${localMin}`;
}

/**
 * Convert a local timezone time string (HH:mm) to UTC time string (HH:mm)
 */
export function localTimeToUtc(localTimeStr, timezone) {
  if (!localTimeStr) return localTimeStr;
  const [h, m] = localTimeStr.split(':').map(Number);
  const now = new Date();
  // Find the timezone offset by comparing a known UTC time in the target timezone
  const ref = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0));
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(ref);
  let refLocalHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
  if (refLocalHour === 24) refLocalHour = 0;
  // offset = local - utc (e.g., EST: 10 - 15 = -5)
  let hourDiff = refLocalHour - h;
  if (hourDiff > 12) hourDiff -= 24;
  if (hourDiff < -12) hourDiff += 24;
  // UTC = local - offset
  let utcH = h - hourDiff;
  if (utcH < 0) utcH += 24;
  if (utcH >= 24) utcH -= 24;
  return `${String(utcH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Format a UTC time string (HH:mm) to 12h display in local timezone with abbreviation
 * e.g. "20:24" UTC with timezone "America/New_York" → "3:24 PM EST"
 */
export function formatScheduleTime(utcTimeStr, timezone) {
  if (!utcTimeStr) return '';
  const localTime = utcTimeToLocal(utcTimeStr, timezone);
  const [hours, minutes] = localTime.split(':');
  const h = parseInt(hours);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${period} ${getTimezoneAbbr(timezone)}`;
}